<template>
  <div
    id="app"
    class="min-h-screen w-screen relative bg-gray-50 dark:bg-gray-900 dark:text-gray-300"
    :class="{
      'h-screen overflow-hidden': isSearchPanelOpen
    }"
  >
    <VitePwaManifest />
    <DarkModeListener />
    <VueQueryDevtools v-if="config.public.environment?.toString()?.includes('DEV')" />
    <GMNotifications
      class="absolute z-40"
      offset-bottom="18"
      offset-top="18"
      offset-right="18"
      offset-left="18"
    />
    <!-- For Tailwind Jit purge to work we need to hardcode the classes here so they're not stripped away -->
    <div class="hidden top-18 right-18 bottom-18 left-18"></div>
    <GMConfirmations class="absolute z-50" />
    <div class="relative z-10">
      <GMAlert v-if="userError">
        Det oppstod en feil ved lasting av brukerkonto: {{ userError }}
      </GMAlert>

      <template v-else>
        <div v-if="!hasLoadedData && $msal.getActiveAccount() != null">
          <Initialize />
        </div>
        <template v-else>
          <AppSidebar />

          <div
            class="transition-all ease-out w-screen min-h-screen view bg-gray-50 dark:bg-gray-900 relative pb-20 flex flex-col"
          >
            <div
              v-show="isSidebarOpen"
              class="fixed top-0 left-0 w-full h-full opacity-50 bg-gray-700 z-30"
              @click="toggleSidebar()"
            ></div>

            <AppHeader class="sticky top-0 z-40" />

            <div class="my-4 h-full flex-1 flex flex-col">
                <NotesPage v-if="isNotesOpen" />
                <slot v-else />
            </div>

            <ClientOnly>
              <AddToHomeScreen />
            </ClientOnly>
          </div>
        </template>
      </template>
    </div>
    <div id="portal-modals" />
  </div>
</template>

<script lang="ts">

import '@gm/components/dist/style.css'
import {confirm, ConfirmationTypes, GMAlert, GMConfirmations, GMNotifications} from '@gm/components'
import {useSidebar} from '@/use/ui/sidebar'
import {useSearchPanel} from '@/use/ui/searchPanel'
import {AddToHomeScreen, AppHeader, AppSidebar} from '#components';
import Initialize from '~/components/Initialize.vue';
import type {PublicClientApplication} from '@azure/msal-browser';
import NotesPage from '~/components/Notes/NotesPage.vue'
import {useNotes} from '~/use/notes/notes'
import {useDraftsDb} from "~/use/indexedDb/draftsDb";
import {useQueueSizeStore} from "~/use/ui/queueSize";
import {VueQueryDevtools} from "@tanstack/vue-query-devtools";
import {useUserService} from "~/services/userService";
import {clearData, getStorageQuota} from "~/helpers/data";
import {getAlwaysAvailableQueryOptions} from "~/helpers/queries";
import {useQueries} from "@tanstack/vue-query";
import {useLocalStorage} from "@vueuse/core";

export default defineComponent({
  name: 'DefaultLayout',
  components: {
    VueQueryDevtools,
    GMAlert,
    NotesPage,
    AppHeader,
    AppSidebar,
    AddToHomeScreen,
    Initialize,
    GMNotifications,
    GMConfirmations
  },
  setup() {
    const hasLoadedDataInitially = useLocalStorage('hasLoadedDataInitially', false)
    const route = useRoute()
    const router = useRouter()
    const { toggleSidebar, isSidebarOpen } = useSidebar()
    const { isSearchPanelOpen } = useSearchPanel()
    const { userQuery } = useUserService()
    const { data: currentUser, error: userError } = userQuery()
    const $msal = useNuxtApp().$msal as PublicClientApplication
    const { draftsDb } = useDraftsDb()
    const { queueSize } = useQueueSizeStore()
    const { t } = useTranslation()
    const { $config: config } = useNuxtApp()

    const { toggleNotes, isNotesOpen, closeNotes } = useNotes()

    watch(route, () => {
      if (isNotesOpen.value) {
        closeNotes()
      }
    })

    let checkStorageUseIntervalId
    const queryOptions = getAlwaysAvailableQueryOptions(currentUser)
    const queries = useQueries({ queries: queryOptions })
    const hasLoadedData = computed(() => {
      return hasLoadedDataInitially.value && queries.value?.some(q => q.data !== undefined)
    })

    const signOut = async (): Promise<void> => {
      await clearData()
      $msal.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      })
    }

    const checkAndHandleStorageUse = () => {
      getStorageQuota().then(async (quota) => {
        if (quota.availableSpace && quota.availableSpace < (1024 * 1024)) { // require at least 1 MB available storage space
          console.warn('Available storage space is low')
          console.log(quota)
          const drafts = await draftsDb.getAll()

          // If no drafts and queue, or user confirms deletion
          // clear data and route to loading
          if (await confirm(
                  t('QUOTA_EXCEEDED_TEXT', {
                    drafts: drafts.length,
                    queue: queueSize.value
                  }) as string,
                  {
                    type: ConfirmationTypes.warning,
                    icon: 'exclamation',
                    options: [
                      {
                        text: t('YES_CLEAN_DATA') as string,
                        value: true,
                        primary: true,
                        iconAfter: 'trash',
                        destructive: true
                      },
                      {
                        text: t('CANCEL') as string,
                        value: false
                      }
                    ]
                  }
              )
          ) {
            await clearData()
            await router.push('/login')
          }
        }
      })
    }


    onMounted(() => {
      if (navigator.onLine) {
        checkAndHandleStorageUse()
      }

      checkStorageUseIntervalId = setInterval(() => {
        if (navigator.onLine) {
          checkAndHandleStorageUse()
        }
      }, 1000 * 60 * 5)
    })

    onBeforeUnmount(() => {
      clearInterval(checkStorageUseIntervalId)
    })

    return {
      config,
      currentUser,
      isSearchPanelOpen,
      isSidebarOpen,
      hasLoadedData,
      hasLoadedDataInitially,
      signOut,
      toggleSidebar,
      userError,
      isNotesOpen,
      toggleNotes,
    }
  },
})
</script>

<style>
.pwa-toast {
  position: fixed;
  right: 0;
  bottom: 0;
  margin: 16px;
  padding: 12px;
  border: 1px solid #8885;
  border-radius: 4px;
  z-index: 1;
  text-align: left;
  box-shadow: 3px 4px 5px 0 #8885;
}
.pwa-toast .message {
  margin-bottom: 8px;
}
.pwa-toast button {
  border: 1px solid #8885;
  outline: none;
  margin-right: 5px;
  border-radius: 2px;
  padding: 3px 10px;
}
</style>
